import type { BadgeTone } from '@/components';
import type { DailyLog } from '@/lib/apiClient';

// Display helpers shared by the per-job Daily Logs tab and the company-wide
// Daily Logs rollup (#617), so a log reads the same wherever it is listed.

// Status → badge tone: draft → warning, submitted → success.
export function statusBadge(status: string): { label: string; tone: BadgeTone } {
  if (status === 'submitted') return { label: 'Submitted', tone: 'success' };
  if (status === 'draft') return { label: 'Draft', tone: 'warning' };
  return { label: status, tone: 'neutral' };
}

// Weekday for the history table / composer header (e.g. "Mon").
export function weekday(iso: string | null | undefined): string {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return '';
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { weekday: 'short' });
}

// One-line weather summary for the history table.
export function weatherSummary(log: Pick<DailyLog, 'weatherCond' | 'weatherHi' | 'weatherLo'>): string {
  const parts: string[] = [];
  if (log.weatherCond) parts.push(log.weatherCond);
  if (log.weatherHi != null || log.weatherLo != null) {
    parts.push(`${log.weatherHi ?? '—'}°/${log.weatherLo ?? '—'}°`);
  }
  return parts.length ? parts.join(' · ') : '—';
}

export function num(v: number | null | undefined): string {
  if (v == null) return '—';
  return Number(v).toLocaleString('en-US', { maximumFractionDigits: 1 });
}

// React Query key prefix for the company-wide Daily Logs rollup. Shared so that
// anything that changes a log can invalidate it: the rollup caches for a minute,
// and without this a log just submitted in its job still reads Draft on the
// rollup — the exact question ("who hasn't submitted?") the page exists to answer.
export const DAILY_LOGS_ROLLUP_KEY = ['daily-logs-rollup'] as const;
